import { APIClientServices } from "./APIClientServices";
import { UserServices } from "./UserServices";
import { WSServices } from "./WSServices";

// DTOs baseados na API
export interface AssistantConversationDTO {
	id: string;
	userId: string;
	title: string | null;
	isArchived: boolean;
	createdAt: string;
	updatedAt: string;
	messagesCount?: number;
}

export type AssistantSenderType = "user" | "assistente" | "confirmation";

export interface AssistantMessageDTO {
	id: string;
	conversationId: string;
	senderType: AssistantSenderType;
	senderRef: string | null;
	content: string;
	metadata: Record<string, any>;
	createdAt: string;
}

export interface AssistantConversationWithMessagesDTO {
	conversation: AssistantConversationDTO;
	messages: AssistantMessageDTO[];
	total: number;
}

export interface SendMessageResult {
	userMessage: AssistantMessageDTO;
	processingId: string;
}

export interface ProcessingStatusDTO {
	processingId?: string;
	conversationId: string;
	status: "idle" | "queued" | "processing" | "waiting_confirmation" | "executing_tool" | "completed" | "error";
	currentStep?: string;
	toolsExecuted?: Array<{ name: string; id: string }>;
	tokensGenerated?: number;
	error?: string;
	result?: {
		userMessage: AssistantMessageDTO;
		assistantMessage: AssistantMessageDTO;
	};
}

// Tipos de eventos SSE
export interface SSEProcessingStarted {
	type: "processing_started";
	userMessage: AssistantMessageDTO;
	processingId: string;
}

export interface SSEToken {
	type: "token";
	token: string;
	content?: string;
}

export interface SSEToolCall {
	type: "tool_call";
	name: string;
	id: string;
	arguments?: Record<string, any>;
}

export interface SSEToolResult {
	type: "tool_result";
	name: string;
	id: string;
	result?: any;
}

export interface SSEConfirmationRequired {
	type: "confirmation_required";
	confirmationId: string;
	toolName: string;
	description: string;
	arguments?: Record<string, any>;
	toolCallId?: string;
	userId?: string;
}

export interface SSEConfirmationResolved {
	type: "confirmation_resolved";
	confirmationId: string;
	approved: boolean;
}

export interface SSEConfirmationMessage {
	type: "confirmation_message";
	conversationId: string;
	message: AssistantMessageDTO;
}

export interface SSEMessageSaved {
	type: "message_saved";
	userMessage?: AssistantMessageDTO;
	assistantMessage?: AssistantMessageDTO;
	leaderMessage?: AssistantMessageDTO;
}

export interface SSEComplete {
	type: "complete";
	content: string;
}

export interface SSEDone {
	type: "done";
}

export interface SSEError {
	type: "error";
	message: string;
}

export type SSEEvent =
	| SSEProcessingStarted
	| SSEToken
	| SSEToolCall
	| SSEToolResult
	| SSEConfirmationRequired
	| SSEConfirmationResolved
	| SSEMessageSaved
	| SSEComplete
	| SSEDone
	| SSEError;

export interface StreamCallbacks {
	onProcessingStarted?: (data: SSEProcessingStarted) => void;
	onToken?: (data: SSEToken) => void;
	onToolCall?: (data: SSEToolCall) => void;
	onToolResult?: (data: SSEToolResult) => void;
	onConfirmationRequired?: (data: SSEConfirmationRequired) => void;
	onConfirmationResolved?: (data: SSEConfirmationResolved) => void;
	onConfirmationMessage?: (data: SSEConfirmationMessage) => void;
	onMessageSaved?: (data: SSEMessageSaved) => void;
	onComplete?: (data: SSEComplete) => void;
	onDone?: (data: SSEDone) => void;
	onError?: (data: SSEError) => void;
	onProcessingStatus?: (data: WSProcessingStatus) => void;
	onProcessingCompleted?: (data: WSProcessingCompleted) => void;
	onProcessingError?: (data: WSProcessingError) => void;
}

/**
 * Tipos de eventos WebSocket emitidos pelo servidor.
 * O servidor emite via `WebsocketService.sendBy(userId, "personal:conversation:{id}:stream", data)`
 * que chega no cliente via `socket.on("receive", { event: "personal:conversation:{id}:stream", data })`.
 */
export interface WSProcessingStatus {
	type: "processing_status";
	conversationId: string;
	processingId: string;
	status: string;
	currentStep?: string;
	toolsExecuted?: Array<{ name: string; id: string }>;
	tokensGenerated?: number;
}

export interface WSProcessingCompleted {
	type: "processing_completed";
	conversationId: string;
	processingId: string;
}

export interface WSProcessingError {
	type: "processing_error";
	conversationId: string;
	processingId: string;
	error: string;
}

const API_URL = APIClientServices.API_URL;

function resolveUrl(base: string, path: string): string {
	try {
		new URL(path);
		return path;
	} catch {}
	const baseUrl = new URL(base);
	if (path.startsWith("/")) {
		baseUrl.pathname = path;
		return baseUrl.toString();
	}
	const basePath = baseUrl.pathname;
	const finalPath = basePath.endsWith("/") ? basePath + path : basePath + "/" + path;
	baseUrl.pathname = finalPath;
	return baseUrl.toString();
}

export class AssistantServices {
	private static readonly BASE_ROUTE = "v1/assistant";

	private static route(workspaceUid?: string | null) {
		if (workspaceUid) {
			return `${this.BASE_ROUTE}/${workspaceUid}`;
		}

		return this.BASE_ROUTE;
	}

	/**
	 * Cria uma nova conversa com o Assistente.
	 */
	static async createConversation(workspaceUid?: string | null, title?: string): Promise<AssistantConversationDTO> {
		return await APIClientServices.post<AssistantConversationDTO>(`${this.route(workspaceUid)}/conversations`, title ? { title } : undefined);
	}

	/**
	 * Lista todas as conversas do usuário com o Assistente.
	 */
	static async listConversations(workspaceUid?: string | null): Promise<AssistantConversationDTO[]> {
		return await APIClientServices.get<AssistantConversationDTO[]>(`${this.route(workspaceUid)}/conversations`);
	}

	/**
	 * Obtém uma conversa específica com o histórico de mensagens.
	 */
	static async getConversation(
		workspaceUid: string | null | undefined,
		conversationId: string,
		limit = 50,
		offset = 0,
	): Promise<AssistantConversationWithMessagesDTO> {
		return await APIClientServices.get<AssistantConversationWithMessagesDTO>(`${this.route(workspaceUid)}/conversations/${conversationId}`, {
			limit: limit.toString(),
			offset: offset.toString(),
		});
	}

	/**
	 * Envia uma mensagem em background (202 Accepted).
	 */
	static async sendMessage(workspaceUid: string | null | undefined, conversationId: string, content: string): Promise<SendMessageResult> {
		return await APIClientServices.post<SendMessageResult>(`${this.route(workspaceUid)}/conversations/${conversationId}/messages`, { content });
	}

	/**
	 * Envia uma mensagem com streaming SSE.
	 * Retorna um AbortController para cancelar o stream.
	 */
	static sendMessageStream(
		workspaceUid: string | null | undefined,
		conversationId: string,
		content: string,
		callbacks: StreamCallbacks,
	): AbortController {
		const controller = new AbortController();

		const url = resolveUrl(API_URL, `${this.route(workspaceUid)}/conversations/${conversationId}/messages/stream`);

		const execute = async () => {
			try {
				const headers: Record<string, string> = {
					"Content-Type": "application/json",
					"X-Requested-From": window.location.href,
				};

				if (UserServices.currentToken) {
					headers["Authorization"] = `Bearer ${UserServices.currentToken}`;
				}

				const response = await fetch(url, {
					method: "POST",
					headers,
					body: JSON.stringify({ content }),
					signal: controller.signal,
				});

				if (!response.ok) {
					const errorText = await response.text();
					let errorMessage = errorText;
					try {
						const errorJson = JSON.parse(errorText);
						errorMessage = errorJson.message || errorText;
					} catch {}
					callbacks.onError?.({ type: "error", message: errorMessage });
					return;
				}

				const reader = response.body?.getReader();
				if (!reader) {
					callbacks.onError?.({ type: "error", message: "Stream não disponível" });
					return;
				}

				const decoder = new TextDecoder();
				let buffer = "";

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;

					buffer += decoder.decode(value, { stream: true });

					const lines = buffer.split("\n");
					buffer = lines.pop() || "";

					let eventType = "";

					for (const line of lines) {
						if (line.startsWith("event: ")) {
							eventType = line.slice(7).trim();
						} else if (line.startsWith("data: ") && eventType) {
							try {
								const data = JSON.parse(line.slice(6));
								switch (eventType) {
									case "processing_started":
										callbacks.onProcessingStarted?.(data);
										break;
									case "token":
										callbacks.onToken?.(data);
										break;
									case "tool_call":
										callbacks.onToolCall?.(data);
										break;
									case "tool_result":
										callbacks.onToolResult?.(data);
										break;
									case "confirmation_required":
										callbacks.onConfirmationRequired?.(data);
										break;
									case "confirmation_resolved":
										callbacks.onConfirmationResolved?.(data);
										break;
									case "message_saved":
										callbacks.onMessageSaved?.(data);
										break;
									case "complete":
										callbacks.onComplete?.(data);
										break;
									case "done":
										callbacks.onDone?.(data);
										break;
									case "error":
										callbacks.onError?.(data);
										break;
									case "processing_status":
										// Status intermediário, pode ser tratado se necessário
										break;
								}
							} catch {
								// Ignora dados malformados
							}
							eventType = "";
						}
					}
				}

				// Stream terminou naturalmente
				callbacks.onDone?.({ type: "done" });
			} catch (error: any) {
				if (error.name !== "AbortError") {
					callbacks.onError?.({ type: "error", message: error?.message ?? "Erro inesperado durante o streaming" });
				}
			}
		};

		execute();
		return controller;
	}

	/**
	 * Obtém o status de processamento de uma conversa.
	 */
	static async getProcessingStatus(workspaceUid: string | null | undefined, conversationId: string): Promise<ProcessingStatusDTO> {
		return await APIClientServices.get<ProcessingStatusDTO>(`${this.route(workspaceUid)}/conversations/${conversationId}/processing`);
	}

	/**
	 * Confirma ou nega a execução de uma ferramenta.
	 * Nota: o endpoint de confirmação NÃO possui workspaceUid no path.
	 */
	static async confirmToolExecution(
		confirmationId: string,
		approved: boolean,
		reason?: string,
	): Promise<{ success: boolean; confirmationId: string; approved: boolean }> {
		return await APIClientServices.post(`${this.BASE_ROUTE}/confirmations/${confirmationId}`, {
			approved,
			reason,
		});
	}

	// ────────────────────────────────────────────────────────────────
	// WebSocket — Assinatura de eventos em tempo real
	// ────────────────────────────────────────────────────────────────

	/**
	 * Gera o nome do evento WebSocket para streaming de uma conversa.
	 * Formato: `personal:conversation:{id}:stream`
	 */
	static getStreamEventName(conversationId: string): string {
		return `personal:conversation:${conversationId}:stream`;
	}

	/**
	 * Inscreve-se nos eventos WebSocket de uma conversa específica.
	 * O servidor emite eventos via `sendBy(userId, "personal:conversation:{id}:stream", data)`.
	 * No cliente, chegam como `receive` events filtrados pelo nome do evento.
	 *
	 * @param conversationId - ID da conversa a escutar
	 * @param callbacks - Callbacks para cada tipo de evento
	 * @returns Objeto com método `stop()` para cancelar a assinatura
	 */
	static subscribeToConversation(conversationId: string, callbacks: StreamCallbacks): { stop: () => void } {
		const eventName = this.getStreamEventName(conversationId);

		const handler = (data: any) => {
			if (!data || !data.type) return;

			switch (data.type) {
				case "processing_started":
					callbacks.onProcessingStarted?.(data);
					break;
				case "token":
					callbacks.onToken?.(data);
					break;
				case "tool_call":
					callbacks.onToolCall?.({
						type: "tool_call",
						name: data.toolCall?.name ?? data.name,
						id: data.toolCall?.id ?? data.id,
						arguments: data.toolCall?.arguments ?? data.arguments,
					});
					break;
				case "tool_result":
					callbacks.onToolResult?.({
						type: "tool_result",
						name: data.functionName ?? data.name,
						id: data.toolCallId ?? data.id,
						result: data.result,
					});
					break;
				case "confirmation_required":
					callbacks.onConfirmationRequired?.({
						type: "confirmation_required",
						confirmationId: data.confirmation?.confirmationId ?? data.confirmationId,
						toolName:
							data.confirmation?.toolName ?? data.toolName ?? data.confirmation?.functionName ?? data.functionName ?? "unknown_tool",
						description: data.confirmation?.description ?? data.description,
						arguments: data.confirmation?.arguments ?? data.arguments,
						toolCallId: data.confirmation?.toolCallId ?? data.toolCallId,
						userId: data.confirmation?.userId ?? data.userId,
					});
					break;
				case "confirmation_resolved":
					callbacks.onConfirmationResolved?.(data);
					break;
				case "confirmation_message":
					callbacks.onConfirmationMessage?.(data);
					break;
				case "complete":
					callbacks.onComplete?.(data);
					break;
				case "message_saved":
					callbacks.onMessageSaved?.(data);
					break;
				case "processing_status":
					callbacks.onProcessingStatus?.(data);
					break;
				case "processing_completed":
					callbacks.onProcessingCompleted?.(data);
					callbacks.onDone?.({ type: "done" });
					break;
				case "processing_error":
					callbacks.onProcessingError?.(data);
					callbacks.onError?.({ type: "error", message: data.error || "Erro no processamento" });
					break;
				case "error":
					callbacks.onError?.(data);
					break;
			}
		};

		const subscription = WSServices.onReceive(eventName, handler);

		return {
			stop: () => {
				subscription.stop();
			},
		};
	}

	/**
	 * Envia uma mensagem em background (202) e automaticamente
	 * assina os eventos WebSocket para receber atualizações em tempo real.
	 *
	 * @param workspaceUid - UID do workspace
	 * @param conversationId - ID da conversa
	 * @param content - Conteúdo da mensagem
	 * @param callbacks - Callbacks para eventos de streaming
	 * @returns Objeto com resultado da mensagem e método `stop()` para encerrar a assinatura WS
	 */
	static async sendMessageWithWS(
		workspaceUid: string | null | undefined,
		conversationId: string,
		content: string,
		callbacks: StreamCallbacks,
	): Promise<{ result: SendMessageResult; stop: () => void }> {
		// Inscreve-se ANTES de enviar a mensagem para não perder eventos
		const subscription = this.subscribeToConversation(conversationId, callbacks);

		try {
			const result = await this.sendMessage(workspaceUid, conversationId, content);
			return { result, stop: subscription.stop };
		} catch (error: any) {
			subscription.stop();
			throw error;
		}
	}
}
